import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Check, Loader2 } from "lucide-react";
import { http } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MediaList } from "./ui";

function useAutosave(product, onSaved) {
  const [data, setData] = useState(product);
  const [state, setState] = useState("idle");
  const timer = useRef(null);
  const first = useRef(true);
  useEffect(() => { setData(product); setState("idle"); first.current = true; }, [product?.id]); // eslint-disable-line
  useEffect(() => {
    if (!data?.id) return;
    if (first.current) { first.current = false; return; }
    setState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const payload = { ...data, retail_price: Number(data.retail_price) || 0, min_order_qty: Number(data.min_order_qty) || 1,
          promo_price: data.promo_price === "" || data.promo_price == null ? null : Number(data.promo_price) };
        const { data: saved } = await http.patch(`/products/${data.id}`, payload);
        setState("saved");
        onSaved?.(saved);
      } catch {
        setState("error");
        toast.error("No se pudo guardar");
      }
    }, 700);
    return () => clearTimeout(timer.current);
  }, [data]); // eslint-disable-line
  return [data, setData, state];
}

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [t, setT] = useState("");
  const add = () => { const v = t.trim(); if (v && !value.includes(v)) onChange([...value, v]); setT(""); };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-neutral-100 text-sm">
            {c}<button type="button" onClick={() => onChange(value.filter((x) => x !== c))}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} data-testid={testid} />
        <Button type="button" variant="outline" onClick={add}><Plus size={14} /></Button>
      </div>
    </div>
  );
}

function PairList({ value = [], onChange, a, b, aKey, bKey, addLabel, testid }) {
  const upd = (i, k, v) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {value.map((x, i) => (
        <div key={i} className="flex gap-2">
          <Input value={x[aKey] ?? ""} placeholder={a} onChange={(e) => upd(i, aKey, aKey === "min_qty" ? Number(e.target.value) : e.target.value)} type={aKey === "min_qty" ? "number" : "text"} />
          <Input value={x[bKey] ?? ""} placeholder={b} type="number" step="0.01" onChange={(e) => upd(i, bKey, e.target.value === "" ? null : Number(e.target.value))} />
          <Button type="button" variant="outline" size="icon" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { [aKey]: aKey === "min_qty" ? 6 : "", [bKey]: null }])} data-testid={`${testid}-add`}>
        <Plus size={14} className="mr-1" /> {addLabel}
      </Button>
    </div>
  );
}

export default function ProductEditor({ product, categories, open, onOpenChange, onSaved }) {
  const [d, setD, state] = useAutosave(product, onSaved);
  if (!d) return null;
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const cat = categories.find((c) => c.slug === d.category);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="product-editor">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl flex items-center justify-between pr-8">
            Editar producto
            <span className="text-xs font-sans font-semibold flex items-center gap-1" data-testid="autosave-state">
              {state === "saving" && <><Loader2 size={12} className="animate-spin" /> Guardando…</>}
              {state === "saved" && <><Check size={12} className="text-emerald-600" /> Guardado</>}
              {state === "error" && <span className="text-red-600">Error al guardar</span>}
            </span>
          </SheetTitle>
          <p className="text-xs text-neutral-500 text-left">Los cambios se guardan automáticamente.</p>
        </SheetHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="flex items-center justify-between sm:col-span-2 bg-neutral-50 rounded-xl p-3">
            <div className="flex items-center gap-2"><Switch checked={d.is_active !== false} onCheckedChange={(v) => set("is_active", v)} data-testid="pe-active" /><span className="text-sm font-semibold">Activo (visible en tienda)</span></div>
            <div className="flex items-center gap-2"><Switch checked={!!d.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pe-featured" /><span className="text-sm font-semibold">Favorito de temporada</span></div>
          </div>
          <div><Label>Código</Label><Input value={d.code} onChange={(e) => set("code", e.target.value)} data-testid="pe-code" /></div>
          <div><Label>Nombre</Label><Input value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="pe-name" /></div>
          <div className="sm:col-span-2"><Label>Descripción</Label><Textarea rows={3} value={d.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="pe-description" /></div>
          <div className="sm:col-span-2"><Label>Características (una por línea)</Label><Textarea rows={3} value={d.features || ""} onChange={(e) => set("features", e.target.value)} placeholder={"Flor artificial de alta calidad\nBase incluida"} data-testid="pe-features" /></div>
          <div>
            <Label>Categoría</Label>
            <select className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" value={d.category} onChange={(e) => setD((p) => ({ ...p, category: e.target.value, subcategory: null }))} data-testid="pe-category">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Subcategoría</Label>
            <select className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" value={d.subcategory || ""} onChange={(e) => set("subcategory", e.target.value || null)} data-testid="pe-subcategory">
              <option value="">—</option>
              {(cat?.subcategories || []).map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          </div>
          <div><Label>Precio minorista (S/)</Label><Input type="number" step="0.01" value={d.retail_price} onChange={(e) => set("retail_price", e.target.value)} data-testid="pe-price" /></div>
          <div><Label>Precio promocional (S/, opcional)</Label><Input type="number" step="0.01" value={d.promo_price ?? ""} onChange={(e) => set("promo_price", e.target.value)} data-testid="pe-promo" /></div>
          <div>
            <Label>Stock</Label>
            <select className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" value={d.stock_status} onChange={(e) => set("stock_status", e.target.value)} data-testid="pe-stock">
              <option value="in_stock">En stock / Entrega inmediata</option>
              <option value="pre_order">Bajo pedido</option>
            </select>
          </div>
          <div><Label>Tiempo de elaboración</Label><Input value={d.production_time || ""} onChange={(e) => set("production_time", e.target.value)} placeholder="Ej. 5-7 días hábiles" /></div>
          <div><Label>Cantidad mínima</Label><Input type="number" value={d.min_order_qty || 1} onChange={(e) => set("min_order_qty", e.target.value)} /></div>
          <div><Label>Contenido (packs)</Label><Input value={d.contents || ""} onChange={(e) => set("contents", e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Precios mayoristas</Label>
            <PairList value={d.wholesale_tiers || []} onChange={(v) => set("wholesale_tiers", v)} a="Desde cantidad" b="Precio S/" aKey="min_qty" bKey="price" addLabel="Agregar nivel" testid="pe-tiers" />
          </div>
          <div className="sm:col-span-2"><Label>Medidas / variantes (precio opcional, reemplaza al minorista)</Label>
            <PairList value={d.variants || []} onChange={(v) => set("variants", v)} a="Ej. 1.5 m" b="Precio S/ (opcional)" aKey="label" bKey="price" addLabel="Agregar variante" testid="pe-variants" />
          </div>
          <div className="sm:col-span-2"><Label>Colores disponibles</Label>
            <TagInput value={d.colors || []} onChange={(v) => set("colors", v)} placeholder="Escribe un color y presiona Enter" testid="pe-colors" />
          </div>
          <div className="sm:col-span-2"><Label>Fotografías</Label><MediaList value={d.images || []} onChange={(v) => set("images", v)} testid="pe-images" /></div>
          <div className="sm:col-span-2"><Label>Videos</Label><MediaList video value={d.videos || []} onChange={(v) => set("videos", v)} testid="pe-videos" /></div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
